'use client'

import { useEffect } from 'react'

import { Chart24, Members24, Money24, X24 } from '@vinted/monochrome-icons'
import {
  Button,
  Cell,
  Dialog,
  Divider,
  Icon,
  Image,
  Navigation,
  Spacer,
  Container,
  Text,
} from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useAsset } from '@marketplace-web/shared/assets'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { clickEvent, viewScreenEvent } from '@marketplace-web/vas/vas-data'

type Props = {
  show: boolean
  showCloseButton?: boolean
  onCloseOrConfirm: () => void
}

const BumpValuePropositionModalV3 = ({ show, showCloseButton = true, onCloseOrConfirm }: Props) => {
  const translate = useTranslate('bump_items.value_proposition_modal_v3')
  const a11yTranslate = useTranslate('bump_items.a11y')
  const asset = useAsset('/assets/bumps')
  const { track } = useTracking()
  const bumpValuePropositionUpdateAbTest = useAbTest('bump_value_proposition_update')

  useTrackAbTest(bumpValuePropositionUpdateAbTest, show)

  useEffect(() => {
    if (!show) return

    track(viewScreenEvent({ screen: 'push_up_value_proposition' }))
  }, [show, track])

  const handleOnClose = () => {
    onCloseOrConfirm()
  }

  const handleOnConfirm = () => {
    track(
      clickEvent({
        screen: 'push_up_value_proposition',
        target: 'choose_your_bump',
      }),
    )

    onCloseOrConfirm()
  }

  return (
    <Dialog
      show={show}
      closeOnOverlay
      defaultCallback={handleOnClose}
      hasScrollableContent
      testId="bump-value-proposition-dialog"
    >
      <div className="u-fill-width">
        <Navigation
          body={translate('title')}
          right={
            <Button
              styling="flat"
              inline
              aria={{ 'aria-label': a11yTranslate('actions.close') }}
              iconName={X24}
              onClick={handleOnClose}
              theme="muted"
              testId="bump-value-proposition-close-button"
            />
          }
        />
        <Divider />
        <ScrollableArea>
          <Container>
            <Image src={asset('bump-value-proposition.png')} scaling="cover" />
            <Spacer size="x-large" />
            <Text as="h2" text={translate('heading')} type="heading" theme="amplified" />
            <Spacer size="x-large" />
            <Cell
              prefix={<Icon color="greyscale-level-2" name={Members24} />}
              title={translate('content.one.title')}
              body={translate('content.one.body')}
              styling="tight"
            />
            <Spacer size="x-large" />
            <Cell
              prefix={<Icon color="greyscale-level-2" name={Money24} />}
              title={translate('content.two.title')}
              body={translate('content.two.body')}
              styling="tight"
            />
            <Spacer size="x-large" />
            <Cell
              prefix={<Icon color="greyscale-level-2" name={Chart24} />}
              title={translate('content.three.title')}
              body={translate('content.three.body')}
              styling="tight"
            />
            {showCloseButton && (
              <>
                <Spacer size="x-large" />
                <Button
                  text={translate('actions.confirm')}
                  styling="filled"
                  onClick={handleOnConfirm}
                  testId="bump-confirm-button"
                />
              </>
            )}
          </Container>
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default BumpValuePropositionModalV3
